import type { ConciergeProductV2Detail } from '../../types';
import type { CatalogV2Offer, CatalogV2Variant } from '../../../types/catalogV2Product';

function maxRewardAmount(offers?: CatalogV2Offer[]): number {
  return Math.max(
    ...(offers ?? [])
      .map((offer) => offer.rewardAmount)
      .filter((amount): amount is number => typeof amount === 'number'),
    0,
  );
}

function promotionFromCompareAt(
  retailPrice?: number,
  compareAtPrice?: number,
): ConciergeProductV2Detail['activeProductPromotion'] {
  if (typeof retailPrice !== 'number' || typeof compareAtPrice !== 'number' || compareAtPrice <= retailPrice) {
    return undefined;
  }
  return {
    isActive: true,
    promoPrice: retailPrice,
    listPrice: compareAtPrice,
    discountPercentage: (compareAtPrice - retailPrice) / compareAtPrice,
    discountAmount: compareAtPrice - retailPrice,
    promoType: '',
    updatedTime: '',
    '@promoUpdatedTime': '',
  };
}

export function resolvePanelDetail(
  detail: ConciergeProductV2Detail | null,
  selectedVariant?: CatalogV2Variant,
): ConciergeProductV2Detail | null {
  if (!detail || !selectedVariant) return detail;
  const primaryOffer = selectedVariant.offers?.find((offer) => offer.isPrimary);
  const rewardAmount = maxRewardAmount(selectedVariant.offers);
  const variantFeatureBullets = selectedVariant.featureBullets?.length ? selectedVariant.featureBullets : undefined;
  return {
    ...detail,
    // Catalog V2 carries TWO skus and the bag needs the CHANNEL one.
    //
    //   detail.stacklineSku          the FAMILY (parent) - not buyable at all
    //   selectedVariant.stacklineSku the catalog variant, e.g. 3R25N0P7J
    //   primaryOffer.stacklineSku    the channel product,  e.g. E3NJ32BNE
    //
    // Only the channel product has an offer, and therefore a price, so it is
    // the only one `CheckoutAdapter.enrichItemsWithMetadata` can resolve. It is
    // also exactly what the storefront PDP puts in the bag, via the same V2
    // lookup (`/catalog-v2/stores/:store/products/:retailerSku`, whose
    // `stacklineSku` is the channel product and whose `catalogSku` is the
    // variant). This field was previously left as the family's, so Concierge
    // sent a parent sku; sending the catalog variant instead is just as
    // unresolvable. Checkout answered both with "Invalid integer: NaN" - no
    // metadata, no price - which the page rendered as "Your bag is empty".
    stacklineSku: primaryOffer?.stacklineSku ?? selectedVariant.stacklineSku,
    title: selectedVariant.title,
    imageUrl: selectedVariant.imageUrl,
    retailPrice: selectedVariant.retailPrice,
    compareAtPrice: selectedVariant.compareAtPrice,
    retailerProductPageUrl: selectedVariant.retailerProductPageUrl,
    retailerId: primaryOffer?.retailerId,
    retailerSku: primaryOffer?.retailerSku,
    retailerName: primaryOffer?.retailerName,
    isAvailable: selectedVariant.isAvailable ?? detail.isAvailable,
    // The store rows come from the variant's offers, so the store policy must
    // come from the variant too.
    checkoutEligible: selectedVariant.checkoutEligible ?? detail.checkoutEligible,
    whereToBuyFallback: selectedVariant.whereToBuyFallback ?? detail.whereToBuyFallback,
    whereToBuyOfferRetailerIds: selectedVariant.whereToBuyOfferRetailerIds ?? detail.whereToBuyOfferRetailerIds,
    featureBullets: variantFeatureBullets ?? detail.featureBullets,
    offers: selectedVariant.offers,
    variationNameValues: selectedVariant.variationNameValues,
    totalRewardAmount: rewardAmount,
    hasRewards: rewardAmount > 0,
    activeProductPromotion: promotionFromCompareAt(selectedVariant.retailPrice, selectedVariant.compareAtPrice),
  };
}
